import React, { useEffect, useState } from "react";
import { Trash2, Send, MessageSquare, X } from "lucide-react";
import { api } from "../../lib/api";

export const AdminMessagesTab = () => {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("");
  const [reply, setReply] = useState({}); // id -> draft text
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const url = filter ? `/admin/contact-messages?status=${filter}` : "/admin/contact-messages";
    const { data } = await api.get(url);
    setItems(data || []);
    setLoading(false);
  };

  useEffect(() => { load().catch(() => setLoading(false)); /* eslint-disable-next-line */ }, [filter]);

  const send = async (m) => {
    const text = (reply[m.id] || "").trim();
    if (!text) return;
    await api.patch(`/admin/contact-messages/${m.id}`, { admin_response: text, status: "replied" });
    setReply({ ...reply, [m.id]: "" });
    load();
  };

  const close = async (m) => {
    await api.patch(`/admin/contact-messages/${m.id}`, { status: "closed" });
    load();
  };

  const remove = async (m) => {
    if (!window.confirm("Delete message permanently?")) return;
    await api.delete(`/admin/contact-messages/${m.id}`);
    load();
  };

  return (
    <section data-testid="admin-messages-section">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <p className="font-mono text-xs tracking-[0.3em] text-kult-yellow inline-flex items-center gap-2">
          <MessageSquare size={14} /> / CONTACT MESSAGES
        </p>
        <div className="flex gap-1 font-mono text-[10px] tracking-[0.25em]" data-testid="messages-filter">
          {["", "new", "replied", "closed"].map((f) => (
            <button
              key={f || "all"}
              onClick={() => setFilter(f)}
              className={`px-3 py-1 border ${filter === f ? "border-kult-pink text-kult-pink" : "border-kult-border text-kult-muted hover:text-white"}`}
              data-testid={`messages-filter-${f || "all"}`}
            >
              {(f || "all").toUpperCase()}
            </button>
          ))}
        </div>
      </div>

      <p className="text-kult-muted text-sm mb-6">
        Messages from the public Contact form AND from customers signed into the portal. Reply here — your response is shown to logged-in customers in their portal.
      </p>

      {loading ? (
        <p className="font-mono text-xs text-kult-muted">LOADING...</p>
      ) : items.length === 0 ? (
        <p className="font-mono text-xs text-kult-muted">No messages.</p>
      ) : (
        <div className="space-y-4" data-testid="messages-list">
          {items.map((m) => (
            <div key={m.id} className="border border-kult-border bg-kult-black p-5" data-testid={`message-row-${m.id}`}>
              <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
                <div>
                  <p className="font-display text-lg">{m.name}</p>
                  <p className="font-mono text-[11px] text-kult-muted">{m.email}</p>
                  {m.order_number && (
                    <p className="font-mono text-[11px] text-kult-pink">↳ {m.order_number}</p>
                  )}
                </div>
                <div className="text-right">
                  <span className={`font-mono text-[10px] tracking-[0.25em] ${m.status === "new" ? "text-kult-pink" : m.status === "replied" ? "text-kult-yellow" : "text-kult-muted"}`}>
                    {m.status?.toUpperCase()}
                  </span>
                  <p className="font-mono text-[10px] text-kult-muted">{new Date(m.created_at).toLocaleString()}</p>
                  <p className="font-mono text-[9px] tracking-[0.25em] text-kult-muted mt-1">
                    {m.source === "customer_portal" ? "VIA PORTAL" : "VIA WEBSITE"}
                  </p>
                </div>
              </div>

              <p className="text-sm text-white whitespace-pre-line border-l-2 border-kult-border pl-3 py-1 mb-3">{m.message}</p>

              {m.admin_response && (
                <div className="mb-3 border-l-2 border-kult-yellow pl-3 py-1">
                  <p className="font-mono text-[10px] tracking-[0.25em] text-kult-yellow mb-1">YOUR REPLY</p>
                  <p className="text-sm text-white whitespace-pre-line">{m.admin_response}</p>
                </div>
              )}

              {m.status !== "closed" && (
                <div className="flex gap-2 flex-wrap items-stretch">
                  <textarea
                    value={reply[m.id] || ""}
                    onChange={(e) => setReply({ ...reply, [m.id]: e.target.value })}
                    placeholder={m.admin_response ? "Send another reply..." : "Type your reply..."}
                    rows={2}
                    className="flex-1 min-w-[200px] bg-transparent border border-kult-border px-3 py-2 font-mono text-sm focus:border-kult-pink outline-none"
                    data-testid={`message-reply-input-${m.id}`}
                  />
                  <div className="flex flex-col gap-2 justify-stretch">
                    <button onClick={() => send(m)} className="bg-kult-pink text-black font-mono text-[10px] tracking-[0.25em] px-4 py-2 inline-flex items-center gap-1 hover:bg-kult-yellow" data-testid={`message-send-${m.id}`}>
                      <Send size={12} /> SEND
                    </button>
                    <button onClick={() => close(m)} className="border border-kult-border text-kult-muted font-mono text-[10px] tracking-[0.25em] px-4 py-2 inline-flex items-center gap-1 hover:text-white" data-testid={`message-close-${m.id}`}>
                      <X size={12} /> CLOSE
                    </button>
                  </div>
                </div>
              )}

              <div className="mt-3 text-right">
                <button onClick={() => remove(m)} className="text-kult-muted hover:text-kult-pink inline-flex items-center gap-1 text-[10px] font-mono tracking-[0.25em]" data-testid={`message-delete-${m.id}`}>
                  <Trash2 size={11} /> DELETE
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
};
